/** Typer som delas mellan Hono-servern och React-appen. */

export type Employee = {
  id: string;
  name: string;
  employee_number: string | null;
  active: boolean;
  sort_order: number;
  vacation_days_per_year: number;
  /**
   * Närvarokod i Fortnox. `TID` för månadsavlönade (arbetstid inom schema),
   * `ARB` för timavlönade. Se `ATTENDANCE_CAUSE_LABELS` i shared/fortnox.
   */
  attendance_code: string;
  /** URL till profilbilden, eller null om ingen bild är uppladdad. */
  photo_url: string | null;
};

export type Shift = {
  id: string;
  employee_id: string;
  work_date: string;
  check_in: string | null;
  check_out: string | null;
  lunch_start: string | null;
  lunch_end: string | null;
  note: string | null;
  manually_edited: boolean;
  exported_at: string | null;
};

export type PunchAction = "check_in" | "lunch_start" | "lunch_end" | "check_out";

/** Härledd status för en anställd just nu. */
export type Status = "out" | "working" | "lunch" | "done";

export const VACATION_KINDS = ["semester", "kompledigt", "tjanstledigt", "foraldraledigt"] as const;

export type VacationKind = (typeof VACATION_KINDS)[number];

export const VACATION_STATUSES = ["vantande", "godkand", "nekad", "aterkallad"] as const;

export type VacationStatus = (typeof VACATION_STATUSES)[number];

export type VacationRequest = {
  id: string;
  employee_id: string;
  employee_name: string;
  start_date: string;
  end_date: string;
  kind: VacationKind;
  /** Antal vardagar ansökan omfattar. */
  days: number;
  message: string | null;
  status: VacationStatus;
  decided_by_name: string | null;
  decided_at: string | null;
  decision_note: string | null;
  created_at: string;
  /** När ledigheten skickades till Fortnox, eller null om den väntar. */
  exported_at: string | null;
};

/**
 * Sammanställning av semesterdagar för en anställd under ett år.
 *
 * `days_*` räknas fram av oss och ser bara det som ansökts här. `fortnox` är
 * hämtat från personalkortet och tar med intjänandeår, sparade dagar och uttag
 * som skett utanför den här appen — det är den siffran som gäller när den finns.
 */
export type VacationBalance = {
  employee_id: string;
  year: number;
  days_per_year: number;
  days_approved: number;
  days_pending: number;
  days_left: number;
  fortnox: FortnoxVacation | null;
};

/** Vad som ännu inte skickats till Fortnox, över alla datum. */
export type PendingExport = {
  /** Avslutade arbetsdagar för anställda med anställningsnummer. */
  shifts: PendingRange;
  /** Godkända ledighetsdagar, summerade. */
  leave: PendingRange;
};

export type PendingRange = {
  count: number;
  oldest: string | null;
  newest: string | null;
};

export type FortnoxVacation = {
  paid_days: number | null;
  saved_days: number | null;
  used_days: number | null;
  pending_days: number | null;
  /** (betalda + sparade) − uttagna − väntande. Null om Fortnox saknar siffror. */
  days_left: number | null;
  fetched_at: string;
};

export type AdminUser = {
  id: string;
  name: string;
  email: string;
  active: boolean;
  last_login_at: string | null;
  has_password: boolean;
};

export type FortnoxStatus = {
  connected: boolean;
  has_credentials: boolean;
  expires_at: string | null;
  last_refreshed_at: string | null;
  scope: string | null;
  redirect_uri: string;
  /** Endast ifyllt när status hämtas med ?test=1. */
  company_name?: string | null;
  test_error?: string | null;
};

/** En anställd som den ser ut i Fortnox — används för att koppla ihop numren. */
/**
 * Saldon som Fortnox räknat ut, cachade hos oss. `fetched_at` visas i
 * gränssnittet — saldot är bara sant vid den tidpunkten.
 *
 * Semesterdagarna kommer från personalkortets semesterflik. `vacation_paid_days`
 * är bruttot för aktuellt semesterår och är alltså inte minskat med uttagna
 * dagar; använd `vacationDaysLeft` för det som faktiskt återstår.
 */
export type EmployeeBalance = {
  employee_number: string;
  flex_hours: number | null;
  comp_hours: number | null;
  /** Dagar att ta ut i år: betalda, obetalda och förskottsdagar. */
  vacation_paid_days: number | null;
  /** Sparade dagar från tidigare semesterår. */
  vacation_saved_days: number | null;
  /** Redan registrerade dagar. */
  vacation_used_days: number | null;
  /** Registrerade men ännu inte färdigbehandlade dagar. */
  vacation_pending_days: number | null;
  fetched_at: string;
};

export type FortnoxEmployee = {
  employee_id: string;
  name: string;
  inactive: boolean;
};

export type ExportResult = {
  sent: string[];
  failures: { shift_id: string; employee_name: string; message: string }[];
};

export type ApiErrorBody = { error: string };
